import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, ShoppingCart, Download, CheckCircle, 
  Maximize2, Home, Building2, Hotel, Briefcase, 
  FileImage, Ruler, Palette, Shield, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import Price from '../components/Price';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Room mockup labels
const ROOM_MOCKUPS = [
  { key: 'bedroom', label: 'Bedroom', icon: Home },
  { key: 'living_room', label: 'Living Room', icon: Home },
  { key: 'hotel', label: 'Hotel Lobby', icon: Hotel },
  { key: 'office', label: 'Office', icon: Briefcase },
];

export default function WallpaperDetailPage() {
  const { productId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [buying, setBuying] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [purchaseId, setPurchaseId] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [showZoom, setShowZoom] = useState(false);

  useEffect(() => { 
    loadProduct(); 
    checkPurchase(); 
  }, [productId]);

  const loadProduct = async () => {
    try { 
      const res = await api.get(`/products/${productId}`); 
      setProduct(res.data); 
    }
    catch { toast.error('Wallpaper not found'); }
  };

  const checkPurchase = async () => {
    try {
      const res = await api.get(`/downloads/check/${productId}`);
      setPurchased(res.data.purchased);
      setPurchaseId(res.data.purchase_id);
    } catch {}
  };

  const handleBuy = async () => {
    if (!user) { navigate('/auth'); return; }
    setBuying(true);
    try {
      const res = await api.post('/payments/checkout', { 
        product_id: productId, 
        origin_url: window.location.origin 
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setBuying(false);
    }
  };

  const handleDownload = () => {
    if (purchaseId) navigate(`/downloads/${purchaseId}`);
    else navigate('/downloads');
  };

  if (!product) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-muted-foreground">Loading wallpaper...</div>
      </div>
    );
  }

  // Get all images for gallery
  const allImages = [
    ...(product.images || []),
    ...(product.preview_gallery || []),
    ...Object.values(product.room_mockups || {}).filter(Boolean)
  ];

  const mainImage = allImages[activeImage] || allImages[0];
  const imageSrc = mainImage?.startsWith('/api/') ? `${BACKEND_URL}${mainImage}` : mainImage;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white" data-testid="wallpaper-detail-page">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <Link 
          to="/marketplace" 
          className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800 transition-colors"
          data-testid="back-to-marketplace"
        >
          <ArrowLeft size={14} /> Back to Marketplace
        </Link>
      </div>

      {/* Hero Section - Full Width Wallpaper Preview */}
      <section className="relative bg-stone-900">
        <div className="max-w-7xl mx-auto">
          <div 
            className="relative aspect-[21/9] md:aspect-[3/1] overflow-hidden cursor-zoom-in group"
            onClick={() => setShowZoom(true)}
            data-testid="wallpaper-hero"
          >
            {imageSrc ? (
              <img 
                src={imageSrc} 
                alt={product.title} 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-800 to-stone-900">
                <FileImage size={64} className="text-stone-600" />
              </div>
            )}
            {/* Zoom hint */}
            <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 size={12} /> Click to zoom
            </div>
            {/* Collection Badge */}
            {product.collection_name && (
              <div className="absolute top-4 left-4">
                <Badge className="bg-white/90 text-stone-800 backdrop-blur-sm border-0">
                  {product.collection_name}
                </Badge>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Room Mockups & Gallery */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title & Style */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-2">
                {product.style_mood || 'Premium Wallpaper'}
              </p>
              <h1 className="font-serif text-3xl md:text-4xl text-stone-900 mb-3">
                {product.title}
              </h1>
              {product.collection_name && (
                <p className="text-stone-500">From the {product.collection_name}</p>
              )}
            </div>

            {/* Room Mockups Grid */}
            {product.room_mockups && Object.keys(product.room_mockups).length > 0 && (
              <div className="space-y-4" data-testid="room-mockups">
                <h3 className="text-xs uppercase tracking-[0.2em] text-stone-500 flex items-center gap-2">
                  <Eye size={14} /> Room Previews
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {ROOM_MOCKUPS.map(room => {
                    const mockupUrl = product.room_mockups[room.key];
                    if (!mockupUrl) return null;
                    const mockupSrc = mockupUrl.startsWith('/api/') ? `${BACKEND_URL}${mockupUrl}` : mockupUrl;
                    return (
                      <div 
                        key={room.key}
                        className="group relative aspect-[4/3] rounded-lg overflow-hidden bg-stone-100 cursor-pointer"
                        onClick={() => {
                          const idx = allImages.indexOf(mockupUrl);
                          if (idx >= 0) setActiveImage(idx);
                          setShowZoom(true);
                        }}
                      >
                        <img 
                          src={mockupSrc} 
                          alt={`${room.label} mockup`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute bottom-3 left-3 flex items-center gap-2 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                          <room.icon size={14} />
                          <span className="text-xs font-medium">{room.label}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Zoom/Detail Preview Gallery */}
            {allImages.length > 1 && (
              <div className="space-y-4" data-testid="preview-gallery">
                <h3 className="text-xs uppercase tracking-[0.2em] text-stone-500 flex items-center gap-2">
                  <Maximize2 size={14} /> Detail Gallery
                </h3>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {allImages.map((img, i) => {
                    const src = img.startsWith('/api/') ? `${BACKEND_URL}${img}` : img;
                    return (
                      <button
                        key={i}
                        onClick={() => setActiveImage(i)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          activeImage === i ? 'border-amber-500 ring-2 ring-amber-200' : 'border-transparent hover:border-stone-300'
                        }`}
                      >
                        <img src={src} alt={`Preview ${i+1}`} className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Premium Description */}
            <div className="space-y-4 bg-stone-50 rounded-xl p-6" data-testid="description-section">
              <h3 className="text-xs uppercase tracking-[0.2em] text-stone-500">About This Wallpaper</h3>
              <p className="text-stone-700 leading-relaxed whitespace-pre-wrap">
                {product.description}
              </p>
            </div>
          </div>

          {/* Right Column - Purchase & Specs */}
          <div className="space-y-6">
            {/* Price & Purchase Card */}
            <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm sticky top-4" data-testid="purchase-card">
              <div className="text-center mb-6">
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">Price</p>
                <p className="font-serif text-4xl text-stone-900">
                  <Price amountEur={product.price} />
                </p>
              </div>

              {purchased ? (
                <Button 
                  onClick={handleDownload} 
                  className="w-full h-12 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="download-btn"
                >
                  <Download size={18} className="mr-2" />
                  Download Your Files
                </Button>
              ) : (
                <Button 
                  onClick={handleBuy}
                  disabled={buying}
                  className="w-full h-12 rounded-lg bg-stone-900 hover:bg-stone-800 text-white"
                  data-testid="buy-btn"
                >
                  {buying ? (
                    <span className="animate-pulse">Processing...</span>
                  ) : (
                    <>
                      <ShoppingCart size={18} className="mr-2" />
                      Purchase Now
                    </>
                  )}
                </Button>
              )}

              {purchased && (
                <div className="flex items-center justify-center gap-2 mt-3 text-emerald-600 text-sm">
                  <CheckCircle size={14} />
                  <span>You own this wallpaper</span>
                </div>
              )}
            </div>

            {/* Specifications */}
            <div className="bg-white rounded-xl border border-stone-200 p-6" data-testid="specs-section">
              <h3 className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4 flex items-center gap-2">
                <Ruler size={14} /> Specifications
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-stone-100">
                  <span className="text-stone-500">Dimensions</span>
                  <span className="text-stone-800 font-medium">{product.dimensions || '3840 × 2160'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-stone-100">
                  <span className="text-stone-500">Resolution</span>
                  <span className="text-stone-800 font-medium">{product.dpi_resolution || '300 DPI'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-stone-100">
                  <span className="text-stone-500">Format</span>
                  <span className="text-stone-800 font-medium">{product.format || 'PNG, JPG'}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-stone-500">Style</span>
                  <span className="text-stone-800 font-medium">{product.style_mood || 'Premium'}</span>
                </div>
              </div>
            </div>

            {/* License */}
            <div className="bg-gradient-to-br from-amber-50 to-stone-50 rounded-xl border border-amber-200/50 p-6" data-testid="license-section">
              <h3 className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-4 flex items-center gap-2">
                <Shield size={14} /> License
              </h3>
              <p className="text-sm text-stone-700 mb-3">
                {product.license_type || 'Personal & Commercial Use'}
              </p>
              <ul className="space-y-2 text-xs text-stone-600">
                <li className="flex items-center gap-2">
                  <CheckCircle size={12} className="text-emerald-500" />
                  Use in personal projects
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={12} className="text-emerald-500" />
                  Use in commercial projects
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={12} className="text-emerald-500" />
                  Print for home or office
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={12} className="text-emerald-500" />
                  Lifetime access
                </li>
              </ul>
            </div>

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag, i) => (
                  <Badge key={i} variant="outline" className="text-xs bg-white border-stone-200 text-stone-600">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Zoom Modal */}
      {showZoom && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setShowZoom(false)}
          data-testid="zoom-modal"
        >
          <button 
            className="absolute top-4 right-4 text-white/70 hover:text-white text-sm"
            onClick={() => setShowZoom(false)}
          >
            Press ESC or click to close
          </button>
          <img 
            src={imageSrc} 
            alt={product.title}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </div>
  );
}
